"use client"

import PaystackPop from "@paystack/inline-js"
import type { InitiateCardPaymentResponse } from "@/lib/types/payment.types"

export type PaystackSuccess = {
  id?: number
  reference: string
  message?: string
}

let paystackInstance: PaystackPop | null = null
const PAYSTACK_OPEN_BODY_CLASS = "paystack-checkout-open"

function getPaystack() {
  if (typeof window === "undefined") {
    throw new Error("Paystack checkout can only run in the browser.")
  }
  if (!paystackInstance) {
    paystackInstance = new PaystackPop()
  }
  return paystackInstance
}

function cleanupBodyClass() {
  document.body.classList.remove(PAYSTACK_OPEN_BODY_CLASS)
}

function beginPaystackCheckout() {
  document.body.classList.add(PAYSTACK_OPEN_BODY_CLASS)
}

function redirectToPaystack(authorizationUrl: string): void {
  cleanupBodyClass()
  window.location.assign(authorizationUrl)
}

type PaystackCallbacks = {
  onSuccess: (response: PaystackSuccess) => void
  onCancel: () => void
  onError: (error: { message?: string }) => void
}

function buildCallbacks(
  resolve: (value: PaystackSuccess) => void,
  reject: (reason: Error) => void,
  authorizationUrl?: string,
): PaystackCallbacks {
  return {
    onSuccess: (response: PaystackSuccess) => {
      cleanupBodyClass()
      resolve(response)
    },
    onCancel: () => {
      cleanupBodyClass()
      reject(new Error("Payment cancelled. You can retry this payment."))
    },
    onError: (error: { message?: string }) => {
      cleanupBodyClass()
      if (authorizationUrl) {
        redirectToPaystack(authorizationUrl)
        return
      }
      reject(
        new Error(error?.message?.trim() || "Unable to open Paystack checkout."),
      )
    },
  }
}

/**
 * Opens Paystack using the backend-initialized session only.
 *
 * Prefer resumeTransaction(accessCode) so the charged amount is exactly what
 * /transaction/initialize created. Never call newTransaction with a recomputed
 * amount — that can diverge from the frozen payment record and break verify.
 * Falls back to authorizationUrl redirect when inline resume fails.
 */
export function openPaystackPopupFromInitiate(
  publicKey: string,
  data: InitiateCardPaymentResponse,
  _email: string,
  _metadata?: Record<string, unknown>,
): Promise<PaystackSuccess> {
  if ((data.amountToPay ?? 0) <= 0) {
    throw new Error("No Paystack payment is required for this transaction.")
  }

  const accessCode = data.accessCode?.trim()
  const authorizationUrl = data.authorizationUrl?.trim()

  if (!accessCode && authorizationUrl) {
    redirectToPaystack(authorizationUrl)
    return Promise.reject(
      new Error("Redirecting to Paystack to complete your payment."),
    )
  }

  if (!accessCode) {
    throw new Error(
      "Payment session is incomplete. Missing Paystack access code. Please try again.",
    )
  }

  // publicKey is unused for resumeTransaction but kept for API compatibility
  void publicKey

  const paystack = getPaystack()
  beginPaystackCheckout()

  return new Promise((resolve, reject) => {
    const callbacks = buildCallbacks(resolve, reject, authorizationUrl)

    try {
      paystack.resumeTransaction(accessCode, callbacks)
    } catch (error) {
      cleanupBodyClass()
      if (authorizationUrl) {
        redirectToPaystack(authorizationUrl)
        reject(new Error("Redirecting to Paystack to complete your payment."))
        return
      }
      reject(
        error instanceof Error
          ? error
          : new Error("Unable to open Paystack checkout."),
      )
    }
  })
}
